import React, { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import PsychologyRoundedIcon from '@mui/icons-material/PsychologyRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import FlipCard from '../components/FlipCard';
import MiniTaskGameHeader from '../components/MiniTaskGameHeader';
import useMiniTaskCountdown from '../useMiniTaskCountdown';
import { playMiniTaskSound } from '../soundEffects';
import { withTurkishGenitive } from '../turkishSuffixes';
import { buildMemoryBoards } from './memoryDeck';
import { loadMemoryCatalog } from './memoryCatalog';
import { getMemoryLayout } from './memoryLayout';

const emptyBoards = [
  { matchedAssetIds: [], attemptCount: 0, completed: false },
  { matchedAssetIds: [], attemptCount: 0, completed: false },
];

function MemoryBackdrop() {
  return (
    <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      <Box sx={{ position: 'absolute', left: '-4%', top: '30%', width: 130, height: 44, borderRadius: 99, bgcolor: 'rgba(255,255,255,0.86)', '&::before, &::after': { content: '""', position: 'absolute', borderRadius: '50%', bgcolor: 'inherit' }, '&::before': { width: 58, height: 58, left: 18, top: -25 }, '&::after': { width: 72, height: 72, right: 12, top: -35 } }} />
      <Box sx={{ position: 'absolute', right: '-3%', top: '38%', width: 118, height: 40, borderRadius: 99, bgcolor: 'rgba(255,255,255,0.84)', '&::before, &::after': { content: '""', position: 'absolute', borderRadius: '50%', bgcolor: 'inherit' }, '&::before': { width: 52, height: 52, left: 16, top: -23 }, '&::after': { width: 64, height: 64, right: 10, top: -31 } }} />
      <Box sx={{ position: 'absolute', left: '-8%', right: '-8%', bottom: -125, height: 220, borderRadius: '50% 50% 0 0', bgcolor: '#a7d84b', transform: 'rotate(-1deg)', boxShadow: 'inset 0 18px 0 rgba(255,255,255,0.14)' }} />
      <Box sx={{ position: 'absolute', left: '56%', right: '-10%', bottom: -105, height: 205, borderRadius: '50% 50% 0 0', bgcolor: '#84c63e', transform: 'rotate(-7deg)' }} />
      <Box sx={{ position: 'absolute', left: { xs: 20, md: 86 }, bottom: { xs: 38, md: 58 }, color: '#facc15', fontSize: { xs: 28, md: 42 }, transform: 'rotate(-14deg)' }}>★</Box>
      <Box sx={{ position: 'absolute', right: { xs: 28, md: 110 }, bottom: { xs: 52, md: 72 }, color: '#fb7185', fontSize: { xs: 25, md: 38 } }}>✿</Box>
      <Box sx={{ position: 'absolute', left: { xs: 20, md: 120 }, top: '45%', color: '#c084fc', fontSize: 28 }}>✦</Box>
      <Box sx={{ position: 'absolute', right: { xs: 25, md: 140 }, top: '49%', color: '#93c5fd', fontSize: 24 }}>✦</Box>
    </Box>
  );
}

function ClosedCardFace() {
  return (
    <>
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          left: '12%',
          top: '13%',
          color: '#a78bfa',
          fontSize: 'clamp(12px, 2vw, 25px)',
          lineHeight: 1,
          transform: 'rotate(-12deg)',
          textShadow: '0 2px 0 rgba(255,255,255,0.8)',
        }}
      >
        ★
      </Box>
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          right: '12%',
          bottom: '12%',
          color: '#c4b5fd',
          fontSize: 'clamp(10px, 1.7vw, 21px)',
          lineHeight: 1,
          transform: 'rotate(14deg)',
          textShadow: '0 2px 0 rgba(255,255,255,0.8)',
        }}
      >
        ★
      </Box>
      <Box
        aria-hidden
        sx={{
          position: 'relative',
          width: '54%',
          aspectRatio: '1',
          borderRadius: '50%',
          bgcolor: 'rgba(255,255,255,0.88)',
          border: '3px solid rgba(167,139,250,0.72)',
          boxShadow: '0 5px 0 rgba(109,40,217,0.12), inset 0 -5px 0 rgba(221,214,254,0.38)',
        }}
      >
        <Box sx={{ position: 'absolute', left: '5%', top: '5%', width: '31%', height: '31%', borderRadius: '50%', bgcolor: '#7c3aed' }} />
        <Box sx={{ position: 'absolute', right: '5%', top: '5%', width: '31%', height: '31%', borderRadius: '50%', bgcolor: '#7c3aed' }} />
        <Box sx={{ position: 'absolute', inset: '10%', borderRadius: '50%', bgcolor: '#fff' }}>
          <Box sx={{ position: 'absolute', left: '13%', top: '27%', width: '29%', height: '34%', borderRadius: '50%', bgcolor: '#6d45df', transform: 'rotate(-18deg)' }}>
            <Box sx={{ position: 'absolute', right: '18%', top: '18%', width: '28%', height: '28%', borderRadius: '50%', bgcolor: '#fff' }} />
          </Box>
          <Box sx={{ position: 'absolute', right: '13%', top: '27%', width: '29%', height: '34%', borderRadius: '50%', bgcolor: '#6d45df', transform: 'rotate(18deg)' }}>
            <Box sx={{ position: 'absolute', left: '18%', top: '18%', width: '28%', height: '28%', borderRadius: '50%', bgcolor: '#fff' }} />
          </Box>
          <Box sx={{ position: 'absolute', left: '43%', top: '57%', width: '14%', height: '10%', borderRadius: '50%', bgcolor: '#4c1d95' }} />
          <Box sx={{ position: 'absolute', left: '38%', top: '66%', width: '24%', height: '13%', borderBottom: '2px solid #6d45df', borderRadius: '0 0 99px 99px' }} />
        </Box>
      </Box>
    </>
  );
}

function MemoryCard({ card, open, matched, disabled, onClick, assetUrls = {} }) {
  const [imageFailed, setImageFailed] = useState(false);
  const visible = open || matched;
  const label = matched
    ? `${card.label}, eşleşti`
    : visible
      ? `${card.label}, açık kart`
      : 'Kapalı kart';

  return (
    <FlipCard data-memory-card className="memory-matching-card" open={open} matched={matched}
      aria-label={label} disabled={disabled} onClick={onClick}
      back={<ClosedCardFace />}
      front={<>
        {imageFailed ? <StarRoundedIcon aria-hidden sx={{ fontSize: 'clamp(34px, 7vw, 72px)', color: '#f59e0b' }} />
          : <Box component="img" src={assetUrls[card.src] || card.src} alt="" onError={() => setImageFailed(true)}
            sx={{ width: '88%', height: '88%', objectFit: 'contain', transform: 'scale(1.18)', filter: matched ? 'saturate(1.08)' : 'none' }} />}
        {matched && <CheckCircleRoundedIcon aria-hidden sx={{ position: 'absolute', right: { xs: 5, md: 9 }, top: { xs: 5, md: 9 },
          color: '#16a34a', bgcolor: '#fff', borderRadius: '50%', fontSize: { xs: 25, md: 36 },
          filter: 'drop-shadow(0 3px 0 rgba(22,101,52,0.16))' }} />}
      </>}
      sx={{
        minWidth: 0, width: '100%', aspectRatio: '1', borderRadius: { xs: 2.2, md: 3.2 },
        transform: matched ? 'scale(0.97)' : 'none', transition: 'transform 180ms ease',
        '&:hover': { transform: disabled || visible ? undefined : 'translateY(-3px)' },
        '&:focus-visible': { outline: '5px solid #facc15', outlineOffset: 3 },
        '& .mini-task-flip-front, & .mini-task-flip-back': {
          borderRadius: 'inherit', overflow: 'hidden',
          boxShadow: '0 7px 0 rgba(109,40,217,0.26), 0 14px 24px rgba(76,29,149,0.17)',
        },
        '& .mini-task-flip-front': {
          border: matched ? '4px solid #22c55e' : '4px solid #c4b5fd',
          background: matched ? '#f0fdf4' : 'linear-gradient(145deg, #ffffff 0%, #faf7ff 100%)',
        },
        '& .mini-task-flip-back': {
          border: '4px solid #8b5cf6',
          background: 'repeating-linear-gradient(-35deg, rgba(255,255,255,0.18) 0, rgba(255,255,255,0.18) 3px, transparent 3px, transparent 12px), linear-gradient(145deg, #ede9fe 0%, #c4b5fd 100%)',
        },
      }}
    />
  );
}

function ProgressDots({ boardIndex }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.8 }}>
      {[0, 1].map((index) => (
        <Box
          key={index}
          sx={{
            width: index === boardIndex ? { xs: 30, md: 42 } : { xs: 13, md: 16 },
            height: { xs: 13, md: 16 },
            borderRadius: 99,
            bgcolor: index < boardIndex ? '#22c55e' : index === boardIndex ? '#7c3aed' : '#ddd6fe',
            transition: 'width 180ms ease, background-color 180ms ease',
          }}
        />
      ))}
    </Box>
  );
}

function CenterState({ icon, title, detail, children }) {
  return (
    <Box sx={{ minHeight: { xs: 350, md: 470 }, display: 'grid', placeItems: 'center', textAlign: 'center', px: 2 }}>
      <Box>
        <Box sx={{ width: { xs: 86, md: 112 }, height: { xs: 86, md: 112 }, mx: 'auto', mb: 2, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: '#f3e8ff', color: '#6d28d9', boxShadow: '0 8px 0 rgba(109,40,217,0.10)' }}>
          {icon}
        </Box>
        <Typography sx={{ color: '#251777', fontWeight: 1000, fontSize: { xs: 34, md: 52 }, lineHeight: 1.04 }}>
          {title}
        </Typography>
        {detail && (
          <Typography sx={{ mt: 1.2, color: '#6d5d93', fontWeight: 900, fontSize: { xs: 19, md: 27 } }}>
            {detail}
          </Typography>
        )}
        {children}
      </Box>
    </Box>
  );
}

export default function MemoryRoundView({
  round,
  boardSessionId,
  classTitle,
  soundEnabled,
  onToggleSound,
  onExit,
  onComplete,
  assetUrls = {},
}) {
  const [decks, setDecks] = useState(null);
  const [boards, setBoards] = useState(emptyBoards);
  const boardsRef = useRef(boards);
  const [boardIndex, setBoardIndex] = useState(0);
  const [openCardIds, setOpenCardIds] = useState([]);
  const [resolving, setResolving] = useState(false);
  const [phase, setPhase] = useState('intro');
  const [error, setError] = useState('');
  const [exitOpen, setExitOpen] = useState(false);
  const completionStarted = useRef(false);
  const finishReasonRef = useRef('completed');
  const remainingSeconds = useMiniTaskCountdown({
    roundId: round.round_id,
    boardSessionId,
    durationSeconds: round.duration_seconds,
    active: Boolean(decks && phase !== 'intro' && phase !== 'completing' && phase !== 'completion_error'),
    onExpire: () => completeRound(boardsRef.current, 'timer'),
    persist: false,
  });
  const pairCounts = useMemo(
    () => (round.deck_pair_counts || [round.pair_count, round.pair_count]).map(Number),
    [round.deck_pair_counts, round.pair_count],
  );

  useEffect(() => {
    let active = true;
    loadMemoryCatalog(round.catalog_version)
      .then((catalog) => {
        if (active) setDecks(buildMemoryBoards(catalog.assets, pairCounts, round.deck_seeds));
      })
      .catch((catalogError) => {
        if (active) setError(catalogError?.message || 'Hafıza kartları yüklenemedi.');
      });
    return () => {
      active = false;
    };
  }, [pairCounts, round.catalog_version, round.deck_seeds]);

  useEffect(() => {
    if (phase !== 'intro') return undefined;
    const timer = window.setTimeout(() => setPhase('playing'), 1100);
    return () => window.clearTimeout(timer);
  }, [phase]);

  const completeRound = async (nextBoards, finishReason = 'completed') => {
    if (completionStarted.current) return;
    completionStarted.current = true;
    finishReasonRef.current = finishReason;
    setPhase('completing');
    setError('');
    try {
      await onComplete(
        nextBoards.map((board) => Number(board.attemptCount)),
        finishReason,
        nextBoards.map((board) => Number(board.matchedAssetIds?.length || 0)),
      );
    } catch (completionError) {
      completionStarted.current = false;
      setError(completionError?.message || 'Sonucun kaydedilemedi.');
      setPhase('completion_error');
    }
  };

  const resolvePair = (first, second) => {
    const matched = first.id === second.id;
    const current = boards[boardIndex];
    const nextCurrent = {
      ...current,
      attemptCount: Number(current.attemptCount) + 1,
      matchedAssetIds: matched
        ? [...new Set([...(current.matchedAssetIds || []), first.id])]
        : current.matchedAssetIds || [],
    };
    const isComplete = matched && nextCurrent.matchedAssetIds.length === pairCounts[boardIndex];
    nextCurrent.completed = isComplete;
    const nextBoards = boards.map((board, index) => (index === boardIndex ? nextCurrent : board));
    boardsRef.current = nextBoards;

    window.setTimeout(() => {
      if (completionStarted.current || finishReasonRef.current === 'timer') return;
      setBoards(nextBoards);
      setOpenCardIds([]);
      setResolving(false);
      if (!isComplete) {
        if (matched) playMiniTaskSound('correct', soundEnabled);
        return;
      }
      playMiniTaskSound('complete', soundEnabled);
      if (boardIndex === 0) {
        setPhase('transition');
        window.setTimeout(() => {
          if (completionStarted.current || finishReasonRef.current === 'timer') return;
          setBoardIndex(1);
          setPhase('playing');
        }, 1200);
      } else {
        completeRound(nextBoards);
      }
    }, matched ? 350 : 900);
  };

  const handleCardClick = (card) => {
    if (resolving || phase !== 'playing' || openCardIds.includes(card.cardId)) return;
    const nextOpen = [...openCardIds, card.cardId];
    setOpenCardIds(nextOpen);
    if (nextOpen.length === 2) {
      setResolving(true);
      const currentDeck = decks[boardIndex];
      resolvePair(
        currentDeck.find((item) => item.cardId === nextOpen[0]),
        currentDeck.find((item) => item.cardId === nextOpen[1]),
      );
    }
  };

  const handleConfirmedExit = () => {
    setExitOpen(false);
    onExit();
  };

  const retryCompletion = () => completeRound(boardsRef.current, finishReasonRef.current);
  const pairCount = pairCounts[boardIndex] || Number(round.pair_count);
  const layout = getMemoryLayout(pairCount);
  const currentBoard = boards[boardIndex] || emptyBoards[boardIndex];
  const matchedIds = new Set(currentBoard.matchedAssetIds || []);
  const matchedCount = matchedIds.size;
  const studentName = round.student?.display_name || 'Öğrenci';

  if (!decks && !error) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: '#fffaf0' }}>
        <CenterState
          icon={<CircularProgress size={48} />}
          title="Kartların hazırlanıyor"
          detail="Birazdan başlayacağız..."
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: '100vw',
        minHeight: '100vh',
        bgcolor: '#fffaf0',
        color: '#172554',
        overflow: 'hidden',
        backgroundImage: 'linear-gradient(180deg, #fffaf0 0%, #fff8ea 70%, #f7f4d6 100%)',
      }}
    >
      {round.preview && (
        <Box sx={{ position: 'fixed', bottom: 12, left: 12, zIndex: 20, bgcolor: '#facc15', color: '#713f12', px: 1.5, py: 0.5, borderRadius: 99, fontWeight: 1000, boxShadow: '0 3px 0 rgba(146,64,14,0.16)' }}>
          TEST MODU
        </Box>
      )}
      <MemoryBackdrop />

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          mx: 'auto',
          maxWidth: 1720,
          px: { xs: 1.2, sm: 2, md: 3.2 },
          pb: { xs: 2, md: 3 },
          '@media (min-width: 600px) and (max-height: 1000px)': { pb: 0 },
        }}
      >
        <MiniTaskGameHeader isEndless={Boolean(round?.is_endless)} classTitle={classTitle} soundEnabled={soundEnabled} onToggleSound={onToggleSound} />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr auto', md: '220px minmax(0, 1fr) 220px' },
            alignItems: 'center',
            gap: { xs: 1, md: 2 },
            mt: { xs: 1.7, md: 3.2 },
            mb: { xs: 3.2, sm: 1.4, md: 2.2 },
          }}
        >
          <ButtonBase
            onClick={() => setExitOpen(true)}
            sx={{
              justifySelf: 'start',
              gridColumn: { xs: 1, md: 1 },
              gridRow: 1,
              borderRadius: 3,
              border: '2px solid #d8b4fe',
              bgcolor: 'rgba(255,255,255,0.88)',
              color: '#33258a',
              px: { xs: 1.4, md: 2.2 },
              py: { xs: 0.85, md: 1.2 },
              minWidth: { xs: 0, sm: 142, md: 210 },
              minHeight: { xs: 52, md: 60 },
              boxShadow: '0 4px 0 rgba(91,33,182,0.10)',
              display: 'flex',
              alignItems: 'center',
              gap: 0.8,
            }}
          >
            <ArrowBackRoundedIcon sx={{ fontSize: { xs: 24, md: 31 } }} />
            <Typography sx={{ fontWeight: 1000, fontSize: { xs: 16, md: 25 }, lineHeight: 1, whiteSpace: 'nowrap' }}>Ana sayfa</Typography>
          </ButtonBase>

          <Typography
            sx={{
              gridColumn: { xs: '1 / -1', md: 2 },
              gridRow: { xs: 2, md: 1 },
              textAlign: 'center',
              color: '#251777',
              fontWeight: 1000,
              fontSize: { xs: 29, sm: 40, md: 48 },
              lineHeight: 1,
              overflowWrap: 'anywhere',
              textShadow: '0 3px 0 rgba(255,255,255,0.94)',
            }}
          >
            {withTurkishGenitive(studentName)} Zihin Görevi
          </Typography>

          <Box sx={{ gridColumn: { xs: 2, md: 3 }, gridRow: 1, justifySelf: 'end', borderRadius: 3, border: '2px solid #d8b4fe', bgcolor: 'rgba(255,255,255,0.88)', color: '#33258a', px: { xs: 1.1, md: 2.2 }, py: { xs: 0.85, md: 1.2 }, minWidth: { xs: 0, sm: 126, md: 210 }, textAlign: 'center', boxShadow: '0 4px 0 rgba(91,33,182,0.10)' }}>
            <Typography sx={{ fontWeight: 1000, fontSize: { xs: 16, md: 25 }, lineHeight: 1, whiteSpace: 'nowrap' }}>Tur {boardIndex + 1} / 2</Typography>
            <Typography role="timer" sx={{ fontWeight: 900, fontSize: { xs: 13, md: 17 } }}>Süre: {remainingSeconds} sn</Typography>
          </Box>
        </Box>

        <Box
          sx={{
            width: { xs: layout.panelWidthXs, sm: '100%' },
            boxSizing: 'border-box',
            maxWidth: 1260,
            mx: 'auto',
            position: 'relative',
            borderRadius: { xs: 3, md: 4 },
            bgcolor: 'rgba(255,255,255,0.96)',
            border: '2px solid #e9d5ff',
            boxShadow: '0 15px 35px rgba(76,29,149,0.14), inset 0 0 0 2px rgba(255,255,255,0.84)',
            px: layout.panelPaddingX,
            pt: { xs: 4.5, md: 5.2 },
            pb: { xs: 1.5, md: 2 },
            minHeight: { xs: 480, md: 590 },
          }}
        >
          <Box sx={{ position: 'absolute', left: '50%', top: 0, transform: 'translate(-50%, -50%)', borderRadius: 2.5, border: '2px solid #d8b4fe', bgcolor: '#f5f0ff', color: '#6d45df', px: { xs: 1.4, md: 2.2 }, py: { xs: 0.55, md: 0.75 }, display: 'flex', alignItems: 'center', gap: 0.8, minWidth: { xs: 190, md: 270 }, justifyContent: 'center', boxShadow: '0 4px 0 rgba(91,33,182,0.10)' }}>
            <PsychologyRoundedIcon sx={{ color: '#7c3aed', fontSize: { xs: 27, md: 34 } }} />
            <Typography sx={{ fontWeight: 1000, fontSize: { xs: 17, md: 23 }, lineHeight: 1 }}>Eşini Bul</Typography>
          </Box>

          {phase === 'intro' && (
            <CenterState
              icon={<PsychologyRoundedIcon sx={{ fontSize: { xs: 56, md: 74 } }} />}
              title="Eşini Bul"
              detail="Kartlara dokun, aynı iki resmi bul."
            >
              <Box sx={{ mt: 2.5 }}><ProgressDots boardIndex={boardIndex} /></Box>
            </CenterState>
          )}

          {phase === 'transition' && (
            <CenterState
              icon={<CheckCircleRoundedIcon sx={{ fontSize: { xs: 58, md: 78 }, color: '#22c55e' }} />}
              title="İlk turu tamamladın!"
              detail="İkinci tur hazırlanıyor..."
            >
              <Box sx={{ mt: 2.5 }}><ProgressDots boardIndex={1} /></Box>
            </CenterState>
          )}

          {(phase === 'completing' || phase === 'completion_error') && (
            <CenterState
              icon={phase === 'completing'
                ? <CircularProgress size={52} />
                : <StarRoundedIcon sx={{ fontSize: { xs: 58, md: 76 }, color: '#f59e0b' }} />}
              title={phase === 'completing' ? 'Harika çalıştın!' : 'Sonucun kaydedilemedi'}
              detail={phase === 'completing' ? 'Sonucun kaydediliyor...' : error}
            >
              {phase === 'completion_error' && (
                <Button variant="contained" onClick={retryCompletion} sx={{ mt: 2.5, borderRadius: 99, px: 4, py: 1.2, bgcolor: '#6d45df', fontWeight: 1000, fontSize: 18 }}>
                  Tekrar dene
                </Button>
              )}
            </CenterState>
          )}

          {phase === 'playing' && decks && (
            <Box sx={{ width: '100%', textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: { xs: 1, md: 2 }, mb: { xs: 1.3, md: 1.8 } }}>
                <Typography sx={{ color: '#4c1d95', fontWeight: 1000, fontSize: { xs: 19, md: 25 } }}>
                  İki aynı resmi bul
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, borderRadius: 99, bgcolor: '#f0fdf4', border: '2px solid #bbf7d0', color: '#15803d', px: 1.4, py: 0.55 }}>
                  <CheckCircleRoundedIcon sx={{ fontSize: { xs: 21, md: 25 } }} />
                  <Typography sx={{ fontWeight: 1000, fontSize: { xs: 16, md: 19 } }}>{matchedCount} / {pairCount} çift</Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  width: layout.boardWidth,
                  maxWidth: layout.boardMaxWidth,
                  mx: 'auto',
                  display: 'grid',
                  gridTemplateColumns: `repeat(${layout.columns}, minmax(0, 1fr))`,
                  gap: layout.gap,
                }}
              >
                {decks[boardIndex].map((card) => (
                  <MemoryCard
                    key={card.cardId}
                    card={card}
                    assetUrls={assetUrls}
                    open={openCardIds.includes(card.cardId)}
                    matched={matchedIds.has(card.id)}
                    disabled={resolving}
                    onClick={() => handleCardClick(card)}
                  />
                ))}
              </Box>

              <Box sx={{ minHeight: { xs: 50, md: 64 }, mt: { xs: 1.2, md: 1.5 }, px: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, color: '#37306f' }}>
                <Box sx={{ width: { xs: 32, md: 38 }, height: { xs: 32, md: 38 }, borderRadius: '50%', bgcolor: '#a78bfa', color: '#fff', display: 'grid', placeItems: 'center', boxShadow: 'inset 0 -3px 0 rgba(15,23,42,0.12)' }}>
                  <FavoriteRoundedIcon sx={{ fontSize: { xs: 18, md: 22 } }} />
                </Box>
                <Typography sx={{ fontWeight: 900, fontSize: { xs: 14, md: 19 }, textAlign: 'center' }}>Kartlara dokun ve aynı iki resmi bul.</Typography>
              </Box>
              <ProgressDots boardIndex={boardIndex} />
            </Box>
          )}

          {error && !decks && (
            <CenterState
              icon={<StarRoundedIcon sx={{ fontSize: { xs: 58, md: 76 }, color: '#f59e0b' }} />}
              title="Kartlar yüklenemedi"
              detail={error}
            >
              <Button variant="outlined" onClick={onExit} sx={{ mt: 2.5, borderRadius: 99, px: 3, fontWeight: 1000 }}>
                Öğrenci listesine dön
              </Button>
            </CenterState>
          )}
        </Box>
      </Box>

      <Dialog open={exitOpen} onClose={() => setExitOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4, border: '2px solid #e9d5ff' } }}>
        <DialogTitle sx={{ color: '#251777', fontWeight: 1000, fontSize: 26 }}>Görevden çıkmak istiyor musun?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#6d5d93', fontWeight: 800 }}>Bu turdaki ilerlemen kaydedilmeyecek.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setExitOpen(false)} autoFocus sx={{ fontWeight: 900 }}>Göreve dön</Button>
          <Button color="error" variant="contained" onClick={handleConfirmedExit} sx={{ borderRadius: 99, fontWeight: 900 }}>Çık</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
